import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomerSpecsTableLayout, Button, DangerButton, SizeTag, LightboxBackdrop, LightboxImage } from '../../styles/CustomerSpecsIndexStyles';
import authAxios from '../../utils/authAxios';
import { getOrderedSizesForCommodity } from '../../utils/sizeOptions';
import {
  NO_PLU_LABEL,
  normalizeUpcOptionPayload,
  upcSelectionsToPayload,
  apiUpcRefsToSelections,
} from '../../utils/customerSpecUpc';
import { isValidManual } from '../../components/ManualUpcField';
import { digitsOnly } from '../../utils/barcodeUtils';
import { hydrateTiered, buildTiered, reTier, canonTieredEntries } from '../../utils/tieredGrades';
import { canonicalCustomerId, zeroBagStyleNames, tiedBagStyles } from '../../utils/bagLabelRules';
import { warnToast } from '../../utils/toastUtils';
import { parseCsvList, toCsv } from '../../utils/csvList';
import { isSpecSelectablePidStatus } from '../../utils/pidStatus';
import StatusPicker from '../../components/StatusPicker';
import { statusRank } from '../../utils/specStatus';
import SpecFormFields from '../../components/SpecFormFields';

const columns = [
  { key: 'Status', label: 'Status' },
  { key: 'CustomerName', label: 'Customer' },
  { key: 'DestinationsCsv', label: 'Destination' },
  { key: 'SpecName', label: 'Spec Name' },
  { key: 'Universal', label: 'Universal', center: true },
  { key: 'DomesticOrImport', label: 'Domestic / Import' },
  { key: 'CommodityName', label: 'Commodity' },
  { key: 'LastEdit', label: 'Last Edit Date', center: true },
];

// parseCsvList / toCsv live in ../../utils/csvList (fix-it M4).

const CustomerSpecsManagePage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const collator = useMemo(() => new Intl.Collator(undefined, { sensitivity: 'base' }), []);
  const [sortConfig, setSortConfig] = useState(null); // { key, direction: 'asc'|'desc' }
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelData, setPanelData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [destExpanded, setDestExpanded] = useState(new Set());
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [showForm] = useState(false); // use Add button to navigate to Form

  // Options and derived maps (same sources as Entry Form for continuity)
  const [customers, setCustomers] = useState([]);
  const [customerRows, setCustomerRows] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [varieties, setVarieties] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [gradesAll, setGradesAll] = useState([]);
  const [styles, setStyles] = useState([]);
  const [labels, setLabels] = useState([]);
  const [bagLabelRows, setBagLabelRows] = useState([]); // dbo.BagLabels — per-style bag-label rules
  const [pluMap, setPluMap] = useState({});
  const [methods, setMethods] = useState([]); // all dbo.Methods — for display resolution
  const [methodOptions, setMethodOptions] = useState([]); // PID-backed Active/Incoming — for the picker
  const [upcOptions, setUpcOptions] = useState([]);
  const [upcRefs, setUpcRefs] = useState([]);
  const [tihi, setTihi] = useState([]);
  const [caseTags, setCaseTags] = useState([]);
  const [pluImagesByLabelId, setPluImagesByLabelId] = useState({}); // LabelID -> image urls (label preview)
  const [pluImagesCache, setPluImagesCache] = useState({}); // PLU UniqueID -> image urls
  const [fieldErrors, setFieldErrors] = useState({});
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const panelOrderedSizes = useMemo(
    () => getOrderedSizesForCommodity(sizes, panelData?.CommodityID, panelData?.DomesticOrImport),
    [sizes, panelData?.CommodityID, panelData?.DomesticOrImport]
  );
  const sizingEnabled = Boolean(panelData?.CommodityID) && panelOrderedSizes.length > 0;
  const lastPanelCommodityRef = React.useRef(null);
  const panelBaselineRef = React.useRef(null); // raw hydrated spec; canonicalized at compare time for the dirty guard (E2)
  const modalRef = React.useRef(null); // edit modal box — for autofocus + Tab focus-trap

  const mergedUpcOptions = upcOptions;
  const upcOptionByValue = useMemo(
    () => new Map([...(upcRefs || []), ...(mergedUpcOptions || [])].map((o) => [o.value, o])),
    [upcRefs, mergedUpcOptions]
  );

  // Derived option memos consumed by the shared <SpecFormFields> (mirror the Entry Form).
  const destinationOptions = useMemo(() => {
    if (!panelData?.CustomerID) return [];
    const chosenName = customers.find(c => String(c.UniqueID) === String(panelData.CustomerID))?.Customer || panelData?.CustomerName;
    if (!chosenName) return [];
    const rows2 = customerRows.filter(r => (r.Customer || '').trim() === (chosenName || '').trim());
    const set = new Set(rows2.map(r => String(r.Destination || '').trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [panelData?.CustomerID, panelData?.CustomerName, customers, customerRows]);

  const varietyOptionsForCommodity = useMemo(() => {
    if (!panelData?.CommodityID) return [];
    const cid = Number(panelData.CommodityID);
    return (varieties || []).filter(v => Number(v.CommodityID) === cid).map(v => v.Variety).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [panelData?.CommodityID, varieties]);

  const gradeOptionsForCommodity = useMemo(() => {
    if (!panelData?.CommodityID) return [];
    const cid = Number(panelData.CommodityID);
    return (gradesAll || []).filter(g => Number(g.Commodity) === cid).map(g => g.GradeName).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [panelData?.CommodityID, gradesAll]);

  const derivedPLUIds = useMemo(() => {
    const set = new Set();
    (panelData?.LabelIDs || [])
      .map(e => (e && typeof e === 'object') ? e.value : e)
      .filter((lbl) => lbl && lbl !== NO_PLU_LABEL)
      .forEach(lbl => (pluMap[lbl] || []).forEach(id => set.add(id)));
    return Array.from(set).sort((a, b) => a - b);
  }, [panelData?.LabelIDs, pluMap]);

  const tihiById = useMemo(() => {
    const map = {};
    (tihi || []).forEach(t => { map[Number(t.UniqueID)] = t.TiHi; });
    return map;
  }, [tihi]);

  // Fetch PLU images for any PIDPLU label selected in the panel (mirror the Entry Form).
  useEffect(() => {
    (async () => {
      const labelVals = (panelData?.LabelIDs || []).map(e => (e && typeof e === 'object') ? e.value : e);
      const lbls = labelVals.filter(l => pluMap[l]);
      if (!lbls.length) return;
      const newLabelToImages = { ...pluImagesByLabelId };
      const newCache = { ...pluImagesCache };
      let changed = false;
      for (const lbl of lbls) {
        const ids = pluMap[lbl] || [];
        const allImages = [];
        for (const id of ids) {
          const key = Number(id);
          let imgs = newCache[key];
          if (!imgs) {
            try {
              const imgRes = await authAxios.get(`/api/pidplu/images?pluId=${key}`);
              imgs = Array.isArray(imgRes.data) ? imgRes.data : [];
            } catch { imgs = []; }
            newCache[key] = imgs;
          }
          imgs.forEach(u => allImages.push(u));
        }
        const unique = Array.from(new Set(allImages));
        if (JSON.stringify(unique) !== JSON.stringify(newLabelToImages[lbl] || [])) {
          newLabelToImages[lbl] = unique;
          changed = true;
        }
      }
      if (changed) setPluImagesByLabelId(newLabelToImages);
      if (Object.keys(newCache).length !== Object.keys(pluImagesCache).length) setPluImagesCache(newCache);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelData?.LabelIDs, pluMap, pluImagesByLabelId, pluImagesCache]);

  useEffect(() => {
    if (!panelData) return;
    if (!sizingEnabled) return;
    const curr = String(panelData.CommodityID || '');
    const prev = String(lastPanelCommodityRef.current || '');
    const commodityChanged = curr && curr !== prev;
    lastPanelCommodityRef.current = curr;

    setPanelData(p => {
      if (!p) return p;
      const allowed = new Set(panelOrderedSizes);
      const next = { ...p };

      // Commodity change clears the selection (sizes don't carry across commodities);
      // otherwise keep only sizes still valid for the current commodity.
      if (commodityChanged) {
        next.RecommendedSizingList = [];
        next.SpecSizeRangeList = [];
      } else {
        next.RecommendedSizingList = (next.RecommendedSizingList || []).filter(v => allowed.has(v));
        next.SpecSizeRangeList = (next.SpecSizeRangeList || []).filter(v => allowed.has(v));
      }

      next.SubbingOptions = (next.SubbingOptions || []).filter(v => allowed.has(v));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizingEnabled, panelOrderedSizes.length, panelData?.DomesticOrImport]);

  // Load options when panel opens (continuity with Entry Form)
  useEffect(() => {
    if (!panelOpen) {
      setOptionsLoaded(false);
      return;
    }
    (async () => {
      try {
        setOptionsLoaded(false);
        const [custDD, custAll, commAll, varRes, sizeDD, gradeRes, packRes, bags, plus, methRes, methOptRes, upcOptRes, tihiRes, tagRes, bagLabelRes] = await Promise.all([
          authAxios.get('/api/customers/dropdown'),
          authAxios.get('/api/customers'),
          authAxios.get('/api/commodities'),
          authAxios.get('/api/varieties'),
          authAxios.get('/api/sizeoptions/dropdown'),
          authAxios.get('/api/grades'),
          authAxios.get('/api/packtypes'),
          authAxios.get('/api/pidbags'),
          authAxios.get('/api/pidplu'),
          authAxios.get('/api/methods'),
          authAxios.get('/api/customer-specs/method-options'),
          authAxios.get('/api/customer-specs/upc-options'),
          authAxios.get('/api/tihi'),
          authAxios.get('/api/casetags'),
          authAxios.get('/api/baglabels'),
        ]);
        setCustomers(custDD.data || []);
        setCustomerRows(custAll.data || []);
        setCommodities(commAll.data || []);
        setVarieties(varRes.data || []);
        setSizes(sizeDD.data || []);
        setGradesAll(gradeRes.data || []);
        setStyles(packRes.data || []);
        // Build union label IDs
        // Curate the label add-pool to Active + Incoming PID labels (a spec may reference a
        // live or coming-soon label; Pending/Inactive are not offered). An already-selected
        // non-selectable label still renders via TieredGradeList displayFor(id)=id, so no
        // separate keep-selected guard is needed here.
        const isSelectable = (s) => isSpecSelectablePidStatus(s);
        const bagLabels = new Set((bags.data || []).filter(b => isSelectable(b.Status)).map(b => String(b.LABEL_ID)).filter(Boolean));
        const pluLabels = new Set((plus.data || []).filter(p => isSelectable(p.Status)).map(p => String(p.LabelID)).filter(Boolean));
        const allLabels = Array.from(new Set([...bagLabels, ...pluLabels])).sort((a, b) => a.localeCompare(b));
        setLabels(allLabels);
        // PLU map for display
        const map = {};
        (plus.data || []).forEach(p => {
          const k = String(p.LabelID || '');
          if (!k) return;
          if (!map[k]) map[k] = new Set();
          map[k].add(p.UniqueID);
        });
        const out = {};
        Object.keys(map).forEach(k => out[k] = Array.from(map[k]).sort((a, b) => a - b));
        setPluMap(out);
        setMethods(methRes.data || []);
        setMethodOptions(methOptRes.data || []);
        const normalizedUpcs = normalizeUpcOptionPayload(upcOptRes.data || {});
        setUpcOptions(normalizedUpcs.options);
        setUpcRefs(normalizedUpcs.refs);
        setTihi(tihiRes.data || []);
        setCaseTags(tagRes.data || []);
        setBagLabelRows(bagLabelRes.data || []);
        setOptionsLoaded(true);
      } catch (e) {
        console.error('[Manage] Load options failed', e);
        setOptionsLoaded(false);
      }
    })();
  }, [panelOpen]);

  // Compute BWCDisplay from type/value
  const computeBwcDisplay = (type, value) => {
    if (type === 'BULK') return 'BULK';
    if (!value) return '';
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    if (type === 'Weight') {
      const kg = num * 0.45359237;
      const fmt = (n) => (n % 1 === 0 ? String(n) : n.toFixed(2));
      return `${fmt(num)}lb (${fmt(Number(kg.toFixed(2)))}kg)`;
    }
    if (type === 'Count') return `${num}CT`;
    return '';
  };

  const normalizeBwcType = (t) => {
    const raw = String(t || '').trim();
    if (!raw) return '';
    const low = raw.toLowerCase();
    if (low === 'bulk' || low === 'bwc' || raw.toUpperCase() === 'BULK') return 'BULK';
    if (low === 'weight') return 'Weight';
    if (low === 'count') return 'Count';
    return raw;
  };

  const deriveBwcValueFromDisplay = (type, display) => {
    const t = normalizeBwcType(type);
    const d = String(display || '').trim();
    if (!t || !d) return '';
    if (t === 'BULK') return '';
    if (t === 'Weight') {
      // e.g. "4lb (1.81kg)" -> "4"
      const m = d.match(/^\s*([0-9]+(?:\.[0-9]+)?)/);
      return m?.[1] ? String(m[1]) : '';
    }
    if (t === 'Count') {
      // e.g. "12CT" -> "12"
      const m = d.match(/([0-9]+)/);
      return m?.[1] ? String(m[1]) : '';
    }
    return '';
  };

  const bwcTypeDep = panelData?.BWCType;
  const bwcValueDep = panelData?.BWCValue;
  useEffect(() => {
    setPanelData(prev => {
      if (!prev) return prev;
      const type = normalizeBwcType(prev.BWCType);
      if (!type) return prev;

      const valueStr = String(prev.BWCValue ?? '').trim();
      const prevDisplay = String(prev.BWCDisplay || '').trim();

      // Bulk always forces display.
      if (type === 'BULK') {
        const next = 'BULK';
        return next === prevDisplay ? prev : { ...prev, BWCType: type, BWCDisplay: next };
      }

      // If user hasn't entered a value, preserve an existing matching display; otherwise clear.
      if (!valueStr) {
        const matches = type === 'Weight'
          ? (/lb/i.test(prevDisplay) || /\bkg\b/i.test(prevDisplay))
          : (type === 'Count' ? /\bCT\b/i.test(prevDisplay) : false);
        if (!prevDisplay || matches) return (type === prev.BWCType ? prev : { ...prev, BWCType: type });
        return { ...prev, BWCType: type, BWCDisplay: '' };
      }

      const next = computeBwcDisplay(type, valueStr);
      return next === prevDisplay ? (type === prev.BWCType ? prev : { ...prev, BWCType: type }) : { ...prev, BWCType: type, BWCDisplay: next };
    });
  }, [bwcTypeDep, bwcValueDep]);

  // Prune dependent selections when commodity changes
  const commodityIdDep = panelData?.CommodityID;
  useEffect(() => {
    if (!commodityIdDep) return;
    if (!optionsLoaded) return;
    const cid = Number(commodityIdDep);
    const allowedVarieties = new Set((varieties || []).filter(v => Number(v.CommodityID) === cid).map(v => v.Variety).filter(Boolean));
    const allowedSizes = new Set(getOrderedSizesForCommodity(sizes || [], cid));
    const allowedGrades = new Set((gradesAll || []).filter(g => Number(g.Commodity) === cid).map(g => g.GradeName).filter(Boolean));
    setPanelData(prev => {
      if (!prev) return prev;
      // If commodity changed while we were computing, don't apply a stale prune.
      if (String(prev.CommodityID || '') !== String(commodityIdDep || '')) return prev;
      return {
        ...prev,
        Varieties: (prev.Varieties || []).filter(v => allowedVarieties.has(v)),
        Grades: (prev.Grades || []).filter(g => allowedGrades.has(g)),
        SubbingOptions: (prev.SubbingOptions || []).filter(s => allowedSizes.has(s)),
      };
    });
  }, [optionsLoaded, commodityIdDep, varieties, sizes, gradesAll]);

  // Re-tier entry grades when the spec's grade set changes (commodity prune mutates
  // Grades, or direct Grades-chip edits). prevPanelGradesRef is primed in onRowClick so
  // loading a different spec never re-tiers its freshly-hydrated grades against the prior one.
  const specGradesForPrune = panelData?.Grades;
  const prevPanelGradesRef = React.useRef(null);
  useEffect(() => {
    const newGrades = specGradesForPrune || [];
    const prev = prevPanelGradesRef.current;
    prevPanelGradesRef.current = newGrades;
    if (prev === null) return;
    setPanelData(p => {
      if (!p) return p;
      const nextStyle = reTier(p.StyleIDs, prev, newGrades);
      const nextLabel = reTier(p.LabelIDs, prev, newGrades);
      const nextMethod = reTier(p.MethodIDs, prev, newGrades);
      if (JSON.stringify(nextStyle) === JSON.stringify(p.StyleIDs)
        && JSON.stringify(nextLabel) === JSON.stringify(p.LabelIDs)
        && JSON.stringify(nextMethod) === JSON.stringify(p.MethodIDs)) return p;
      return { ...p, StyleIDs: nextStyle, LabelIDs: nextLabel, MethodIDs: nextMethod };
    });
  }, [specGradesForPrune]);

  useEffect(() => {
    (async () => {
      try {
        // Manage shows every status (the Index-only default returns Active).
        const res = await authAxios.get('/api/customer-specs?status=all');
        setRows(res.data || []);
      } catch (e) {
        console.error('[Manage] load specs failed', e);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return rows.filter(r =>
      Object.values({
        Status: r.Status,
        CustomerName: r.CustomerName,
        Universal: r.Universal ? 'true' : 'false',
        DestinationsCsv: r.DestinationsCsv,
        SpecName: r.SpecName,
        DomesticOrImport: r.DomesticOrImport,
        CommodityName: r.CommodityName,
        VarietiesCsv: r.VarietiesCsv,
        BWCDisplay: r.BWCDisplay,
        SizesCsv: r.SizesCsv,
        GradesCsv: r.GradesCsv,
        StyleIDsCsv: r.StyleIDsCsv,
        LabelIDsCsv: r.LabelIDsCsv,
        MethodIDsCsv: r.MethodIDsCsv,
        UPCIDsCsv: r.UPCIDsCsv,
        PIDBagIDsCsv: r.PIDBagIDsCsv,
        PalletTypesCsv: r.PalletTypesCsv,
        TiHiIDsCsv: r.TiHiIDsCsv,
        CaseTagName: r.CaseTagName,
      }).some(v => String(v || '').toLowerCase().includes(q))
    );
  }, [rows, searchQuery]);

  const sorted = useMemo(() => {
    if (!sortConfig) return filtered;
    const { key, direction } = sortConfig;
    const isDate = key === 'LastEdit';
    const copy = [...filtered];
    copy.sort((a, b) => {
      if (isDate) {
        const aTs = a.UpdatedAt ? new Date(a.UpdatedAt).getTime() : (a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0);
        const bTs = b.UpdatedAt ? new Date(b.UpdatedAt).getTime() : (b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0);
        const cmp = aTs - bTs; // oldest->newest
        return direction === 'asc' ? cmp : -cmp; // asc=oldest->newest, desc=newest->oldest
      }
      if (key === 'Status') {
        const cmp = statusRank(a.Status) - statusRank(b.Status); // asc = Active first
        return direction === 'asc' ? cmp : -cmp;
      }
      const aVal = String(a[key] ?? '').trim();
      const bVal = String(b[key] ?? '').trim();
      const cmp = collator.compare(aVal, bVal);
      return direction === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortConfig, collator]);

  const handleSort = (key) => {
    setSortConfig(prev => {
      const same = prev?.key === key;
      const current = same ? prev?.direction : null;
      const isDate = key === 'LastEdit';
      // cycle: null -> (date? desc : asc) -> opposite -> null
      if (!current) return { key, direction: isDate ? 'desc' : 'asc' };
      if (current === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  const onRowClick = async (row) => {
    try {
    setEditingId(row.UniqueID);
      // Load full record for condensed edit panel
      const res = await authAxios.get(`/api/customer-specs/${row.UniqueID}`);
      const raw = res.data || {};
      lastPanelCommodityRef.current = String(raw.CommodityID || '');
      const arrFromCsv = (csv) => String(csv || '').split(',').map(s => String(s).trim()).filter(Boolean);
      const numArrFromCsv = (csv) => arrFromCsv(csv).map(n => Number(n)).filter(v => !Number.isNaN(v));
      const bwcType = normalizeBwcType(raw.BWCType);
      const bwcDisplay = String(raw.BWCDisplay || '').trim();
      const bwcValue = deriveBwcValueFromDisplay(bwcType, bwcDisplay);
      const data = {
        ...raw,
        CustomerName: row.CustomerName || raw.CustomerName || '',
        Destinations: parseCsvList(raw.DestinationsCsv),
        Varieties: arrFromCsv(raw.VarietiesCsv),
        // Sizing: range sliders persist expanded lists; default to legacy SizesCsv if needed
        RecommendedSizingList: parseCsvList(raw.RecommendedSizingCsv || raw.SizesCsv),
        SpecSizeRangeList: parseCsvList(raw.SpecSizeRangeCsv),
        SubbingOptions: parseCsvList(raw.SubbingOptionsCsv),
        ApprovalNeeded: String(raw.ApprovalNeeded || ''),
        SizingNotes: String(raw.SizingNotes || ''),
        Grades: arrFromCsv(raw.GradesCsv),
        // Hydrate tiered entries from the resolved, position-aligned GET assignments
        // (fall back to legacy id arrays). Grades are final from the server — never re-default.
        StyleIDs: hydrateTiered(raw.StyleAssignments, raw.StyleIDs, Number),
        LabelIDs: hydrateTiered(raw.LabelAssignments, raw.LabelIDs, String),
        MethodIDs: hydrateTiered(raw.MethodAssignments, raw.MethodIDs, Number),
        PalletTypes: arrFromCsv(raw.PalletTypesCsv),
        TiHiIDs: numArrFromCsv(raw.TiHiIDsCsv),
        BWCType: bwcType || raw.BWCType || '',
        BWCDisplay: (bwcType === 'BULK' ? 'BULK' : bwcDisplay),
        BWCValue: bwcValue,
        Universal: !!raw.Universal,
        upcSelectionKeys: apiUpcRefsToSelections(raw.UPCRefs?.length ? raw.UPCRefs : (raw.UPCIDs || []), raw.PIDBagIDs || []),
        upcMode: (Array.isArray(raw.ManualUPCs) && raw.ManualUPCs.length) ? 'custom' : 'catalog',
        manualUpc: (Array.isArray(raw.ManualUPCs) && raw.ManualUPCs[0]) ? String(raw.ManualUPCs[0]) : '',
      };
      prevPanelGradesRef.current = data.Grades; // prime so the prune effect no-ops on load
      panelBaselineRef.current = data; // dirty-guard baseline (E2); canonicalized at compare time
      setPanelData(data);
      setPanelOpen(true);
    } catch (e) {
      console.error('[Manage] load single spec failed', e);
    }
  };

  const handleSubmit = async (e) => {
    // Table's form submit is unused now that edits happen in the panel
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this Customer Specification?')) return;
    try {
      setDeleting(true);
      await authAxios.delete(`/api/customer-specs/${id}`);
      setRows(prev => prev.filter(r => r.UniqueID !== id));
      setPanelOpen(false);
      setPanelData(null);
      setEditingId(null);
    } catch (e) {
      console.error('[Manage] delete failed', e);
    } finally {
      setDeleting(false);
    }
  };

  // Inline status flip from the table (after the in-app ConfirmPopover). Optimistic
  // row update + a lightweight PATCH; revert + alert on failure. Reconciles UpdatedAt/
  // UpdatedBy from the server response so the Last Edit column reflects the change.
  const handleStatusChange = async (row, next) => {
    const id = row.UniqueID;
    const prevStatus = row.Status;
    setRows(rs => rs.map(r => (r.UniqueID === id ? { ...r, Status: next } : r)));
    try {
      const res = await authAxios.patch(`/api/customer-specs/${id}/status`, { Status: next });
      const upd = res.data || {};
      setRows(rs => rs.map(r => (r.UniqueID === id
        ? { ...r, Status: upd.Status ?? next, UpdatedAt: upd.UpdatedAt ?? r.UpdatedAt, UpdatedBy: upd.UpdatedBy ?? r.UpdatedBy }
        : r)));
    } catch (e) {
      console.error('[Manage] status change failed', e);
      setRows(rs => rs.map(r => (r.UniqueID === id ? { ...r, Status: prevStatus } : r)));
      alert(e?.response?.data?.error || 'Failed to update status.');
    }
  };

  const closePanel = () => {
    lastPanelCommodityRef.current = null;
    setPanelOpen(false);
    setPanelData(null);
    setEditingId(null);
    setOptionsLoaded(false);
    // Style-keyed errors (bag ties) would otherwise outline a same-id style in the
    // next spec opened.
    setFieldErrors({});
  };

  // Stable canonical projection of the editable panel state for the dirty guard (E2).
  // Mirrors the Form: tiered entries via canonTieredEntries (ignore grade order /
  // ALL-collapse), sizing compared as the resolved size LIST (a range and its list
  // fallback are equivalent), BWCDisplay ignored (derived from type/value). This avoids
  // phantom-dirty from the post-hydration normalization effects (BWC, sizing-range init).
  const canonPanel = (p) => {
    if (!p) return '';
    const orderedSizes = getOrderedSizesForCommodity(sizes, p.CommodityID);
    const sortByOrder = (list) => {
      const idx = new Map(orderedSizes.map((s, i) => [s, i]));
      return (Array.isArray(list) ? list : [])
        .filter((s) => idx.has(s))
        .sort((a, b) => idx.get(a) - idx.get(b));
    };
    return JSON.stringify({
      Universal: !!p.Universal,
      Status: String(p.Status || ''),
      DomesticOrImport: String(p.DomesticOrImport || ''),
      Destinations: p.Destinations || [],
      CommodityID: String(p.CommodityID || ''),
      Varieties: p.Varieties || [],
      BWCType: normalizeBwcType(p.BWCType),
      BWCValue: String(p.BWCValue ?? ''),
      Grades: p.Grades || [],
      StyleIDs: canonTieredEntries(p.StyleIDs, p.Grades),
      LabelIDs: canonTieredEntries(p.LabelIDs, p.Grades),
      MethodIDs: canonTieredEntries(p.MethodIDs, p.Grades),
      Recommended: sortByOrder(p.RecommendedSizingList),
      SpecRange: sortByOrder(p.SpecSizeRangeList),
      SubbingOptions: p.SubbingOptions || [],
      ApprovalNeeded: String(p.ApprovalNeeded || ''),
      SizingNotes: String(p.SizingNotes || ''),
      PalletTypes: p.PalletTypes || [],
      TiHiIDs: p.TiHiIDs || [],
      TemperatureRecorder: String(p.TemperatureRecorder || ''),
      PackagingNotes: String(p.PackagingNotes || ''),
      QualityNotes: String(p.QualityNotes || ''),
      ShippingNotes: String(p.ShippingNotes || ''),
      BagLabelNotes: String(p.BagLabelNotes || ''),
      CustomerItemNumber: String(p.CustomerItemNumber || ''),
      GTIN: String(p.GTIN || ''),
      TechnicalNotes: String(p.TechnicalNotes || ''),
      CaseTagID: p.CaseTagID == null ? '' : String(p.CaseTagID),
      upcSelectionKeys: p.upcSelectionKeys || [],
      upcMode: p.upcMode || 'catalog',
      manualUpc: digitsOnly(p.manualUpc),
    });
  };

  // Guarded close: confirm before discarding unsaved edits (backdrop / X / Cancel).
  const requestClose = () => {
    const dirty = panelData && panelBaselineRef.current
      && canonPanel(panelBaselineRef.current) !== canonPanel(panelData);
    if (dirty && !window.confirm('Discard unsaved changes to this specification?')) return;
    closePanel();
  };

  const savePanel = async () => {
    if (!editingId || !panelData) return;
    // Required fields (Customer is read-only in Edit): Domestic/Import + Commodity.
    const missing = {};
    if (!panelData.DomesticOrImport) missing.DomesticOrImport = true;
    if (!panelData.CommodityID) missing.CommodityID = true;
    if (Object.keys(missing).length) {
      setFieldErrors(missing);
      alert('Please complete all required fields (Domestic/Import and Commodity).');
      return;
    }
    setFieldErrors({});
    // A custom UPC, if entered, must be a valid UPC-A/EAN-13 (length is not enough).
    if (panelData.upcMode === 'custom' && digitsOnly(panelData.manualUpc) && !isValidManual(panelData.manualUpc)) {
      alert('Enter a valid 12-digit UPC-A or 13-digit EAN-13, or switch back to catalog.');
      return;
    }
    {
      const bagCustId = canonicalCustomerId(customers, panelData);
      const bagCommodityId = Number(panelData.CommodityID) > 0 ? Number(panelData.CommodityID) : null;
      // Bag-tie block: a Bag style on Default whose rules tie resolves to no label and
      // the ambiguity is resolvable here, so the save is refused until one is picked.
      const ties = tiedBagStyles(panelData.StyleIDs, styles, bagLabelRows, bagCustId, bagCommodityId);
      if (ties.length) {
        setFieldErrors({ BagLabelTieStyleIds: ties.map((t) => t.id) });
        alert(`Select a bag label for tied style(s): ${ties.map((t) => t.name).join(', ')}`);
        return;
      }
      // Bag-zero warning (warn, don't block): flag Bag-type styles resolving to no label.
      const zeroBagStyles = zeroBagStyleNames(panelData.StyleIDs, styles, bagLabelRows, bagCustId, bagCommodityId);
      if (zeroBagStyles.length) warnToast(`No bag label resolved for Bag style(s): ${zeroBagStyles.join(', ')}`);
    }
    try {
      setSaving(true);
      const recommendedList = panelData.RecommendedSizingList || [];
      const specRangeList = panelData.SpecSizeRangeList || [];
      const recommendedCsv = (() => { const s = toCsv(recommendedList); return s ? s : null; })();
      const specRangeCsv = (() => { const s = toCsv(specRangeList); return s ? s : null; })();
      const subbingCsv = (() => { const s = toCsv(panelData.SubbingOptions || []); return s ? s : null; })();

      // Build payload expected by backend
      const payload = {
        CustomerID: panelData.CustomerID ? Number(panelData.CustomerID) : null,
        Status: panelData.Status || 'Active',
        DestinationsCsv: (()=>{ const s=toCsv(panelData.Destinations || []); return s ? s : null; })(),
        DomesticOrImport: String(panelData.DomesticOrImport || ''),
        Universal: !!panelData.Universal,
        CommodityID: panelData.CommodityID ? Number(panelData.CommodityID) : null,
        VarietiesCsv: (panelData.Varieties || []).length ? (panelData.Varieties || []).join(',') : null,
        BWCType: panelData.BWCType ? String(panelData.BWCType) : null,
        BWCDisplay: panelData.BWCDisplay ? String(panelData.BWCDisplay) : null,
        // Transitional compatibility: keep legacy SizesCsv in sync with RecommendedSizingCsv
        SizesCsv: recommendedCsv,
        RecommendedSizingCsv: recommendedCsv,
        SpecSizeRangeCsv: specRangeCsv,
        SubbingOptionsCsv: subbingCsv,
        ApprovalNeeded: panelData.ApprovalNeeded ? String(panelData.ApprovalNeeded) : null,
        SizingNotes: panelData.SizingNotes ? String(panelData.SizingNotes) : null,
        TechnicalNotes: panelData.TechnicalNotes ? String(panelData.TechnicalNotes) : null,
        GradesCsv: (()=>{ const s=(panelData.Grades || []).join(','); return s ? s : null; })(),
        PalletTypesCsv: (()=>{ const s=(panelData.PalletTypes || []).join(','); return s ? s : null; })(),
        TiHiIDsCsv: (()=>{ const s=(panelData.TiHiIDs || []).join(','); return s ? s : null; })(),
        PackagingNotes: panelData.PackagingNotes ? String(panelData.PackagingNotes) : null,
        BagLabelNotes: panelData.BagLabelNotes ? String(panelData.BagLabelNotes) : null,
        CustomerItemNumber: panelData.CustomerItemNumber ? String(panelData.CustomerItemNumber) : null,
        QualityNotes: panelData.QualityNotes ? String(panelData.QualityNotes) : null,
        GTIN: panelData.GTIN ? String(panelData.GTIN) : null,
        TemperatureRecorder: panelData.TemperatureRecorder ? String(panelData.TemperatureRecorder) : null,
        ShippingNotes: panelData.ShippingNotes ? String(panelData.ShippingNotes) : null,
        CaseTagID: panelData.CaseTagID ? Number(panelData.CaseTagID) : null,
        StyleIDs: buildTiered(panelData.StyleIDs, Number),
        LabelIDs: buildTiered(panelData.LabelIDs, String),
        MethodIDs: buildTiered(panelData.MethodIDs, Number),
        ...(() => {
          // Mutually exclusive: custom mode persists only the typed code (catalog cleared);
          // catalog mode persists the picked refs (manual cleared).
          if (panelData.upcMode === 'custom') {
            const code = digitsOnly(panelData.manualUpc);
            return { UPCRefs: [], UPCIDs: [], PIDBagIDs: [], ManualUPCs: code ? [code] : [] };
          }
          const u = upcSelectionsToPayload(panelData.upcSelectionKeys || []);
          return { UPCRefs: u.UPCRefs, UPCIDs: u.UPCIDs, PIDBagIDs: u.PIDBagIDs, ManualUPCs: [] };
        })(),
        ActorName: String(panelData.UpdatedBy || panelData.CreatedBy || 'Unknown'),
      };

      await authAxios.put(`/api/customer-specs/${editingId}`, payload);
      const res = await authAxios.get('/api/customer-specs?status=all');
      setRows(res.data || []);
      closePanel();
    } catch (e) {
      console.error('[Manage] save failed', e);
      const message = e?.response?.data?.error || 'Failed to save changes.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  // Autofocus the modal on open, and trap Tab within it (a11y). Portaled dropdown menus
  // render at document.body (outside modalRef), so their keyboard nav is unaffected.
  useEffect(() => {
    if (!panelOpen) return;
    const t = setTimeout(() => { try { modalRef.current?.focus?.(); } catch {} }, 30);
    return () => clearTimeout(t);
  }, [panelOpen]);

  const onModalKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); requestClose(); return; }
    if (e.key !== 'Tab') return;
    const el = modalRef.current;
    if (!el) return;
    const nodes = Array.from(el.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(n => n.offsetParent !== null);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  // Reference data + derived option memos passed to the shared field body (mode="edit").
  const refs = {
    customers, commodities, styles, labels, methods, methodOptions, tihi, caseTags,
    pluMap, pluImagesByLabelId,
    destinationOptions, sizeOptionsForCommodity: panelOrderedSizes, varietyOptionsForCommodity,
    gradeOptionsForCommodity, derivedPLUIds, mergedUpcOptions, upcOptionByValue, tihiById,
    bagLabels: bagLabelRows,
  };

  return (
    <>
    <CustomerSpecsTableLayout
      title="Customer Specifications - Manage"
      fitToViewport
      viewportOffset={170}
      showForm={showForm}
      onToggleForm={() => navigate('/CustomerSpecs/Form')}
      onSubmit={handleSubmit}
      columns={columns}
      formData={formData}
      handleChange={(e)=>setFormData(p=>({ ...p, [e.target.name]: e.target.value }))}
      rows={sorted}
      sortConfig={sortConfig}
      onSort={handleSort}
        editingId={null}
        hideActionsColumn
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      getRowProps={(row, index) => ({ onClick: () => onRowClick(row), style: { cursor: 'pointer' }, $index: index })}
        renderCell={(key, row) => {
          if (key === 'Status') {
            return (
              <StatusPicker
                status={row.Status}
                confirm
                size="sm"
                ariaLabel={`Status for ${row.CustomerName || 'spec'}`}
                onChange={(s) => handleStatusChange(row, s)}
              />
            );
          }
          if (key === 'LastEdit') {
            const by = row.UpdatedBy || row.CreatedBy || '—';
            const dtRaw = row.UpdatedAt || row.CreatedAt;
            const dateStr = dtRaw ? new Date(dtRaw).toLocaleDateString('en-US') : '—';
            return (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center' }}>
                <div style={{ fontWeight:600 }}>{dateStr}</div>
                <div style={{ color:'var(--shadow-a65)' }}>{by}</div>
              </div>
            );
          }
          if (key === 'Universal') {
            return row.Universal ? 'True' : 'False';
          }
          if (['DestinationsCsv','VarietiesCsv','SizesCsv','GradesCsv','PalletTypesCsv','LabelIDsCsv','StyleIDsCsv','MethodIDsCsv','UPCIDsCsv','PIDBagIDsCsv','TiHiIDsCsv'].includes(key)) {
            const itemsRaw = key === 'DestinationsCsv' ? parseCsvList(row[key]) : String(row[key] || '').split(',').filter(Boolean);
            const items = (itemsRaw || []).map(v => v === '__ALL__' ? 'All Destinations' : v);

            if (key === 'DestinationsCsv') {
              // If "All Destinations" selected, show just that
              if (items.includes('All Destinations')) {
                return (
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <SizeTag style={{ background:'var(--grey-600)' }}>All Destinations</SizeTag>
                  </div>
                );
              }
              const expanded = destExpanded.has(row.UniqueID);
              const maxShown = 3;
              const visible = expanded ? items : items.slice(0, maxShown);
              const remaining = Math.max(0, items.length - maxShown);
              return (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                  {visible.map((v,i) => <SizeTag key={`${key}-${i}`} style={{ background:'var(--grey-600)' }}>{v}</SizeTag>)}
                  {remaining > 0 && !expanded ? (
                    <button
                      type="button"
                      aria-label="Show more destinations"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDestExpanded(prev => { const n = new Set(prev); n.add(row.UniqueID); return n; });
                      }}
                      style={{
                        width: 26, height: 26, borderRadius: 13,
                        background:'var(--green-800)', color:'var(--white)', border:'none',
                        display:'inline-flex', alignItems:'center', justifyContent:'center',
                        fontWeight: 800, cursor:'pointer'
                      }}
                    >
                      +
                    </button>
                  ) : null}
                  {expanded && items.length > maxShown ? (
                    <button
                      type="button"
                      aria-label="Show fewer destinations"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDestExpanded(prev => { const n = new Set(prev); n.delete(row.UniqueID); return n; });
                      }}
                      style={{
                        width: 26, height: 26, borderRadius: 13,
                        background:'var(--grey-700)', color:'var(--white)', border:'none',
                        display:'inline-flex', alignItems:'center', justifyContent:'center',
                        fontWeight: 800, cursor:'pointer'
                      }}
                    >
                      −
                    </button>
                  ) : null}
                </div>
              );
            }

            return (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {items.map((v,i) => <SizeTag key={`${key}-${i}`} style={{ background:'var(--grey-600)' }}>{v}</SizeTag>)}
              </div>
            );
          }
          return row[key] ?? '';
        }}
      />

      {panelOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Edit Specification"
          onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
          style={{ position:'fixed', inset:0, background:'var(--shadow-a35)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'3vh 16px', overflowY:'auto' }}
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            onKeyDown={onModalKeyDown}
            style={{ width:'1100px', maxWidth:'96vw', maxHeight:'94vh', background:'var(--cream-50)', borderRadius:12, boxShadow:'0 12px 40px var(--shadow-a35)', display:'flex', flexDirection:'column', overflow:'hidden', outline:'none' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'14px 18px', borderBottom:'1px solid var(--shadow-a08)', background:'var(--cream-100)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0, flexWrap:'wrap' }}>
                <h2 style={{ margin:0, fontSize:'1.15rem' }}>Edit Specification</h2>
                {panelData ? (
                  <StatusPicker
                    status={panelData.Status}
                    size="sm"
                    ariaLabel="Specification status"
                    onChange={(s) => setPanelData(p => ({ ...p, Status: s }))}
                  />
                ) : null}
                {panelData ? (
                  <div aria-label="Universal toggle" style={{ display:'inline-flex', background:'var(--shadow-a08)', borderRadius:10, padding:4 }}>
                    <button
                      type="button"
                      onClick={() => setPanelData(p => ({ ...p, Universal: false, Destinations: (p.Destinations || []).includes('__ALL__') ? [] : (p.Destinations || []) }))}
                      style={{ border:'none', cursor:'pointer', padding:'6px 14px', borderRadius:8, background: panelData.Universal ? 'transparent' : 'var(--white)', color:'var(--color-text)', boxShadow: panelData.Universal ? 'none' : '0 2px 6px var(--shadow-a15)' }}
                    >
                      DC Specific
                    </button>
                    <button
                      type="button"
                      onClick={() => setPanelData(p => ({ ...p, Universal: true, Destinations: ['__ALL__'] }))}
                      style={{ border:'none', cursor:'pointer', padding:'6px 14px', borderRadius:8, background: panelData.Universal ? 'var(--white)' : 'transparent', color:'var(--color-text)', boxShadow: panelData.Universal ? '0 2px 6px var(--shadow-a15)' : 'none' }}
                    >
                      All Destinations
                    </button>
                  </div>
                ) : null}
              </div>
              <button aria-label="Close" onClick={requestClose} style={{ background:'transparent', border:'none', fontSize:'20px', cursor:'pointer' }}>×</button>
            </div>

            <div style={{ flex:1, overflowY:'auto', padding:'16px 18px' }}>
              {panelData ? (
                <SpecFormFields
                  mode="edit"
                  form={panelData}
                  setForm={setPanelData}
                  refs={refs}
                  fieldErrors={fieldErrors}
                  onLightbox={setLightboxSrc}
                />
              ) : (
                <div>Loading...</div>
              )}
            </div>

            {panelData ? (
              <div style={{ display:'flex', gap:8, justifyContent:'space-between', padding:'12px 18px', borderTop:'1px solid var(--shadow-a08)', background:'var(--cream-100)' }}>
                <DangerButton type="button" onClick={() => handleDelete(editingId)} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </DangerButton>
                <div style={{ display:'flex', gap:8 }}>
                  <Button type="button" onClick={requestClose} style={{ background: 'var(--grey-500)' }}>Cancel</Button>
                  <Button type="button" onClick={savePanel} disabled={saving} style={{ padding: '8px 20px', fontSize: '0.9rem' }}>{saving ? 'Saving…' : 'Save'}</Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
      {lightboxSrc && (
        <LightboxBackdrop onClick={() => setLightboxSrc(null)}>
          <LightboxImage src={lightboxSrc} alt="Preview" />
        </LightboxBackdrop>
      )}
    </>
  );
};

export default CustomerSpecsManagePage;


